import { GraphQLErrors } from "@apollo/client/errors";
import {
  DataConnectionId,
  GraphQLErrorCode,
  getErrorMessagesFromErrors,
  getErrorMessagesWithCode,
} from "@hex/common";
import { BroadcastChannel } from "broadcast-channel";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { useQueryParams } from "../route/routes";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export type OAuthCallbackBroadcastMessage = {
  success?: {
    linkedDataConnectionIds?: DataConnectionId[];
  };
};

export interface OAuthSuccessViewProps {
  fetchAccessToken: ({
    variables,
  }: {
    variables: {
      code: string;
      state: string;
    };
  }) => Promise<{
    linkedDataConnectionIds?: DataConnectionId[];
    errors?: GraphQLErrors;
  }>;
  getLinkedDataConnectionIds?: ({
    oAuthConnectionId,
  }: {
    oAuthConnectionId: string;
  }) => Promise<DataConnectionId[]>;
  broadcastChannel: string;
}

export const OAuthSuccessView: React.ComponentType<OAuthSuccessViewProps> =
  React.memo(function OAuthSuccessView({
    broadcastChannel,
    fetchAccessToken,
    getLinkedDataConnectionIds,
  }: OAuthSuccessViewProps) {
    const queryParams = useQueryParams();
    const code = queryParams.get("code");
    const state = queryParams.get("state");

    // These query params are provided when using SSO for OAuth.
    const oAuthConnectionId = queryParams.get("oAuthConnectionId");
    const skipTokenFetch = queryParams.get("skipTokenFetch") === "true";

    const [accessTokenResult, setAccessTokenResult] = useState<boolean | null>(
      null,
    );
    const [userFacingError, setUserFacingError] = useState<string | null>(null);

    useEffect(() => {
      let isCancelled = false;
      async function finishAuth() {
        if (code && state) {
          await fetchAccessToken({
            variables: { code, state },
          })
            .then(async ({ errors, linkedDataConnectionIds }) => {
              if (isCancelled) return;

              if (errors && errors.length > 0) {
                const userFacingErrors = getErrorMessagesFromErrors(
                  errors,
                  GraphQLErrorCode.USER_FACING_ERROR,
                );
                if (userFacingErrors.length > 0) {
                  setUserFacingError(userFacingErrors[0]);
                }
                setAccessTokenResult(false);
                return;
              }

              setAccessTokenResult(true);
              const events =
                new BroadcastChannel<OAuthCallbackBroadcastMessage>(
                  broadcastChannel,
                );
              await events.postMessage({
                success: { linkedDataConnectionIds },
              });
              await events.close();
              window.close();
            })
            .catch((e) => {
              if (isCancelled) return;

              const userFacingErrors = getErrorMessagesWithCode(
                e,
                GraphQLErrorCode.USER_FACING_ERROR,
              );
              if (userFacingErrors.length > 0) {
                setUserFacingError(userFacingErrors[0]);
              }
              setAccessTokenResult(false);
              console.error(e);
            });
        } else if (skipTokenFetch) {
          let linkedDataConnectionIds: DataConnectionId[] = [];
          if (getLinkedDataConnectionIds && oAuthConnectionId) {
            linkedDataConnectionIds = await getLinkedDataConnectionIds({
              oAuthConnectionId,
            });
          }
          if (isCancelled) return;

          const events = new BroadcastChannel<OAuthCallbackBroadcastMessage>(
            broadcastChannel,
          );
          await events.postMessage({ success: { linkedDataConnectionIds } });
          await events.close();
          window.close();
        }
      }
      void finishAuth();
      return () => {
        isCancelled = true;
      };
    }, [
      broadcastChannel,
      code,
      fetchAccessToken,
      getLinkedDataConnectionIds,
      oAuthConnectionId,
      skipTokenFetch,
      state,
    ]);

    let resultString: string;
    if (!skipTokenFetch && accessTokenResult == null) {
      resultString = "OAuth handshake in progress";
    } else if (skipTokenFetch || accessTokenResult) {
      resultString = "OAuth handshake completed successfully";
    } else {
      resultString = "OAuth handshake failed";
      if (userFacingError) {
        resultString += `: ${userFacingError}`;
      }
    }
    return <Container>{resultString}</Container>;
  });
